import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AnimatePresence, motion, usePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'

import {
  InterviewType,
  RecallStatus,
  ShortInterview,
} from '@pkg/types/interview'

import { formatApplicationStage } from '@/helpers/ats'
import { dateFromTimestamp } from '@/helpers/date'
import {
  useCreateInterview,
  useDeleteInterview,
  useIntakeFilterOptions,
  useInterviewFilterOptions,
  useShortInterviewsList,
} from '@/react-query/interview/interview'
import { useIsATSConnected, useIsViewOnlyMember } from '@/react-query/team'
import { useUser } from '@/react-query/user'
import { DeleteIcon } from '@/views/assets/DeleteIcon'
import { LinkedinAvatarFallback } from '@/views/assets/LinkedinAvatarFallback'
import { PlusIcon } from '@/views/assets/PlusIcon'
import Card from '@/views/components/Card/Card'
import { PremiumHeader } from '@/views/components/PremiumHeader'
import { useDocumentTitle } from '@/views/hooks/useDocumentTitle'
import {
  EVENT_FILTER_KEY,
  INTERVIEW_TYPE_FILTER_KEY,
  INTERVIEWER_FILTER_KEY,
  JOB_TITLE_FILTER_KEY,
  NAME_FILTER_KEY,
} from '@/views/screens/Home/FilterHeader/const'

import { NotetakerInfo } from '../../components/NotetakerInfoModal'

import { FilterHeader } from './FilterHeader/FilterHeader'
import { useWaitForRecallFinish } from './useWaitForRecallFinish'
import { useWaitForTranscriptGeneration } from './useWaitForTranscriptGeneration'

const Home: React.FC = () => {
  useDocumentTitle('Home')

  const user = useUser()

  const isReadOnly = (user.data?.availableInterviewTranscriptions || 0) <= 0

  const isViewOnlyUser = useIsViewOnlyMember()
  const createInterview = useCreateInterview()

  const [selectedTab, setSelectedTab] = useState<InterviewType>('NORMAL')

  const navigate = useNavigate()

  const handleCreateInterview = async () => {
    const interview = await createInterview.mutateAsync({
      type: selectedTab === 'INTAKE' ? 'INTAKE' : 'NORMAL',
    })
    navigate(`/interview/${interview.id}`)
  }

  const [nameFilter, setNameFilter] = useState<string>('')
  const [jobTitleFilter, setJobTitleFilter] = useState<string>('')
  const [eventFilter, setEventFilter] = useState<string>('')
  const [interviewerFilter, setInterviewerFilter] = useState<string>('')

  const [intakeNameFilter, setIntakeNameFilter] = useState<string>('')
  const [intakeJobTitleFilter, setIntakeJobTitleFilter] = useState<string>('')
  const [intakeEventFilter, setIntakeEventFilter] = useState<string>('')
  const [intakeInterviewerFilter, setIntakeInterviewerFilter] =
    useState<string>('')

  // Debounce the filter values by 500 milliseconds
  const [debouncedNameFilter] = useDebounce(nameFilter, 500)
  const [debouncedIntakeNameFilter] = useDebounce(intakeNameFilter, 500)
  const [debouncedEventFilter] = useDebounce(eventFilter, 500)
  const [debouncedIntakeEventFilter] = useDebounce(intakeEventFilter, 500)

  // Prepare filters using the debounced filter values
  const filters = useMemo(
    () => ({
      nameFilter:
        selectedTab === 'INTAKE'
          ? debouncedIntakeNameFilter
          : debouncedNameFilter,
      jobTitleFilter:
        selectedTab === 'INTAKE' ? intakeJobTitleFilter : jobTitleFilter,
      eventFilter:
        selectedTab === 'INTAKE'
          ? debouncedIntakeEventFilter
          : debouncedEventFilter,
      interviewerFilter:
        selectedTab === 'INTAKE' ? intakeInterviewerFilter : interviewerFilter,
      typeFilter: (selectedTab === 'INTAKE'
        ? 'INTAKE'
        : 'NORMAL') as InterviewType,
    }),
    [
      debouncedNameFilter,
      debouncedIntakeNameFilter,
      intakeJobTitleFilter,
      jobTitleFilter,
      debouncedEventFilter,
      debouncedIntakeEventFilter,
      interviewerFilter,
      intakeInterviewerFilter,
      selectedTab,
    ],
  )

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.nameFilter ||
          filters.jobTitleFilter ||
          filters.eventFilter ||
          filters.interviewerFilter,
      ),
    [filters],
  )

  const shortInterviewsList = useShortInterviewsList(filters)

  const interviews =
    shortInterviewsList.data?.pages.flatMap((page) => page?.interviews ?? []) ||
    []

  // Check if any interviews have meeting names (for allowlisted users)
  const hasAnyMeetingNames = interviews.some(
    (interview) => interview.meetingName && interview.meetingName.trim() !== '',
  )

  // get stored filters from localstorage.
  // Doing this here (instead of in the FilterHeader component) because
  // it prevents blinking of the filter values when the component mounts.
  useEffect(() => {
    const storageInterviewTypeFilter = localStorage.getItem(
      INTERVIEW_TYPE_FILTER_KEY,
    )
    if (storageInterviewTypeFilter) {
      setSelectedTab(storageInterviewTypeFilter as InterviewType)
    }

    const storageNameFilter = localStorage.getItem(NAME_FILTER_KEY)
    if (storageNameFilter) {
      setNameFilter(storageNameFilter)
    }
    const storageJobTitleFilter = localStorage.getItem(JOB_TITLE_FILTER_KEY)
    if (storageJobTitleFilter) {
      setJobTitleFilter(storageJobTitleFilter)
    }
    const storageEventFilter = localStorage.getItem(EVENT_FILTER_KEY)
    if (storageEventFilter) {
      setEventFilter(storageEventFilter)
    }
    const storageInterviewerFilter = localStorage.getItem(
      INTERVIEWER_FILTER_KEY,
    )
    if (storageInterviewerFilter) {
      setInterviewerFilter(storageInterviewerFilter)
    }

    const storageIntakeInterviewerFilter = localStorage.getItem(
      INTERVIEWER_FILTER_KEY + '-intake',
    )
    if (storageIntakeInterviewerFilter) {
      setIntakeInterviewerFilter(storageIntakeInterviewerFilter)
    }
    const storageIntakeJobTitleFilter = localStorage.getItem(
      JOB_TITLE_FILTER_KEY + '-intake',
    )
    if (storageIntakeJobTitleFilter) {
      setIntakeJobTitleFilter(storageIntakeJobTitleFilter)
    }
    const storageIntakeEventFilter = localStorage.getItem(
      EVENT_FILTER_KEY + '-intake',
    )
    if (storageIntakeEventFilter) {
      setIntakeEventFilter(storageIntakeEventFilter)
    }
    const storageIntakeNameFilter = localStorage.getItem(
      NAME_FILTER_KEY + '-intake',
    )
    if (storageIntakeNameFilter) {
      setIntakeNameFilter(storageIntakeNameFilter)
    }
  }, [])
  const hasTeam = !!user.data?.TeamMember

  const { data: filterOptions, isPending: isFilterOptionsPending } =
    useInterviewFilterOptions()
  const { data: intakeFilterOptions, isPending: isIntakeFilterOptionsPending } =
    useIntakeFilterOptions()

  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  )
  const [loadMoreTrigger, setLoadMoreTrigger] = useState<HTMLDivElement | null>(
    null,
  )
  const [isLoadMoreTriggerInView, setIsLoadMoreTriggerInView] = useState(false)
  const hasTriggeredLoadMoreRef = useRef(false)
  const pendingScrollIntentRef = useRef(false)
  const [scrollIntentTick, setScrollIntentTick] = useState(0)
  const lastScrollTopRef = useRef(0)
  const lastTouchYRef = useRef<number | null>(null)

  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainer(node)
  }, [])
  const loadMoreTriggerRef = useCallback((node: HTMLDivElement | null) => {
    setLoadMoreTrigger(node)
  }, [])

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading,
  } = shortInterviewsList

  useEffect(() => {
    hasTriggeredLoadMoreRef.current = false
    pendingScrollIntentRef.current = false
    lastScrollTopRef.current = 0
    lastTouchYRef.current = null
    setScrollIntentTick(0)
  }, [filters])

  const registerScrollIntent = useCallback(() => {
    pendingScrollIntentRef.current = true
    hasTriggeredLoadMoreRef.current = false
    setScrollIntentTick((tick) => tick + 1)
  }, [])

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const currentTarget = event.currentTarget
      const isScrollingDown = currentTarget.scrollTop > lastScrollTopRef.current
      lastScrollTopRef.current = currentTarget.scrollTop
      if (!isScrollingDown) return

      registerScrollIntent()
    },
    [registerScrollIntent],
  )

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (event.deltaY <= 0) return
      registerScrollIntent()
    },
    [registerScrollIntent],
  )

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const firstTouch = event.touches[0]
      if (!firstTouch) {
        lastTouchYRef.current = null
        return
      }
      lastTouchYRef.current = firstTouch.clientY
    },
    [],
  )

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const firstTouch = event.touches[0]
      if (!firstTouch || lastTouchYRef.current === null) {
        return
      }
      if (firstTouch.clientY < lastTouchYRef.current) {
        registerScrollIntent()
      }
      lastTouchYRef.current = firstTouch.clientY
    },
    [registerScrollIntent],
  )

  const handleTouchEnd = useCallback(() => {
    lastTouchYRef.current = null
  }, [])

  // Observer only tracks visibility - doesn't trigger fetches directly
  useEffect(() => {
    if (!loadMoreTrigger) {
      setIsLoadMoreTriggerInView(false)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry?.isIntersecting ?? false
        setIsLoadMoreTriggerInView(isIntersecting)
        if (!isIntersecting) {
          hasTriggeredLoadMoreRef.current = false
          pendingScrollIntentRef.current = false
        }
      },
      {
        root: scrollContainer ?? null,
        rootMargin: '0px 0px 200px 0px',
      },
    )

    observer.observe(loadMoreTrigger)
    return () => observer.disconnect()
  }, [loadMoreTrigger, scrollContainer])

  // Separate effect decides when to fetch based on visibility + query state
  useEffect(() => {
    if (!isLoadMoreTriggerInView) return
    if (hasTriggeredLoadMoreRef.current && !pendingScrollIntentRef.current) {
      return
    }
    if (!hasNextPage) return
    if (isInitialLoading) return
    if (isFetchingNextPage) return

    hasTriggeredLoadMoreRef.current = true
    pendingScrollIntentRef.current = false
    void fetchNextPage()
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
    isLoadMoreTriggerInView,
    scrollIntentTick,
  ])

  useEffect(() => {
    if (!scrollContainer) return
    if (!hasNextPage) return
    if (isInitialLoading || isFetchingNextPage) return
    if (hasTriggeredLoadMoreRef.current) return

    const canScroll =
      scrollContainer.scrollHeight > scrollContainer.clientHeight + 8
    if (canScroll) return

    hasTriggeredLoadMoreRef.current = true
    void fetchNextPage()
  }, [
    fetchNextPage,
    hasNextPage,
    interviews.length,
    isFetchingNextPage,
    isInitialLoading,
    scrollContainer,
  ])

  return (
    <Box pt="40px" flexGrow={1} display="flex" flexDirection="column" minH={0}>
      {!isViewOnlyUser && (
        <Box mb="55px" display="flex" alignItems="center" gap={2}>
          <Button
            variant="brand-solid"
            leftIcon={<PlusIcon stroke="white" />}
            isDisabled={isReadOnly}
            isLoading={createInterview.isPending}
            onClick={handleCreateInterview}
          >
            new {selectedTab === 'INTAKE' ? 'intake' : 'interview'}
          </Button>
          <Text fontWeight="500" display={{ base: 'none', sm: 'block' }}>
            or
          </Text>
          <Box display={{ base: 'none', sm: 'block' }}>
            <NotetakerInfo variant="brand-solid-gray" isDisabled={isReadOnly} />
          </Box>
        </Box>
      )}
      {!isViewOnlyUser && <PremiumHeader />}
      <Card className="flex flex-col flex-grow bg-[#ebebeb] min-h-0">
        <Card className="flex flex-col flex-grow p-2 md:px-[36px] md:py-[20px] relative overflow-x-auto min-h-0">
          {!(
            (selectedTab === 'INTAKE' && isIntakeFilterOptionsPending) ||
            (selectedTab === 'NORMAL' && isFilterOptionsPending) ||
            isInitialLoading
          ) && (
            <motion.div
              style={{ flexShrink: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <FilterHeader
                jobTitles={
                  selectedTab === 'INTAKE'
                    ? intakeFilterOptions?.jobTitles
                    : filterOptions?.jobTitles
                }
                interviewers={
                  selectedTab === 'INTAKE'
                    ? intakeFilterOptions?.interviewers
                    : filterOptions?.interviewers
                }
                nameFilter={
                  selectedTab === 'INTAKE' ? intakeNameFilter : nameFilter
                }
                jobTitleFilter={
                  selectedTab === 'INTAKE'
                    ? intakeJobTitleFilter
                    : jobTitleFilter
                }
                eventFilter={
                  selectedTab === 'INTAKE' ? intakeEventFilter : eventFilter
                }
                interviewerFilter={
                  selectedTab === 'INTAKE'
                    ? intakeInterviewerFilter
                    : interviewerFilter
                }
                setNameFilter={
                  selectedTab === 'INTAKE' ? setIntakeNameFilter : setNameFilter
                }
                setJobTitleFilter={
                  selectedTab === 'INTAKE'
                    ? setIntakeJobTitleFilter
                    : setJobTitleFilter
                }
                setEventFilter={
                  selectedTab === 'INTAKE'
                    ? setIntakeEventFilter
                    : setEventFilter
                }
                setInterviewerFilter={
                  selectedTab === 'INTAKE'
                    ? setIntakeInterviewerFilter
                    : setInterviewerFilter
                }
                selectedTab={selectedTab}
                hasTeam={hasTeam}
                hasEvents={
                  selectedTab === 'INTAKE'
                    ? intakeFilterOptions?.hasEvents
                    : filterOptions?.hasEvents
                }
              />
            </motion.div>
          )}

          {isInitialLoading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Skeleton
                  height="40px"
                  my="10px"
                  key={index}
                  rounded={'full'}
                  endColor="#d7d7d7"
                  startColor="#f5f5f5"
                />
              ))}
            </Box>
          ) : (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedTab}
                  id="blabla"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  className="flex flex-col flex-grow min-h-0"
                >
                  <Box
                    ref={scrollContainerRef}
                    className="flex flex-col flex-grow min-h-0"
                    overflowY="auto"
                    data-testid="home-interview-scroll"
                    onScroll={handleScroll}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {interviews.length > 0 ? (
                      <>
                        <AnimatePresence mode={'sync'}>
                          {interviews.map((interview) => (
                            <InterviewCard
                              key={interview.id}
                              interview={interview}
                              hasTeam={hasTeam}
                              selectedTab={selectedTab}
                              hasEvents={
                                selectedTab === 'INTAKE'
                                  ? intakeFilterOptions?.hasEvents
                                  : filterOptions?.hasEvents
                              }
                            />
                          ))}
                        </AnimatePresence>

                        {isFetchingNextPage && (
                          <Box>
                            {[...Array(3)].map((_, index) => (
                              <Skeleton
                                height="40px"
                                my="10px"
                                key={index}
                                rounded={'full'}
                                endColor="#f5f5f5"
                                startColor="#d7d7d7"
                              />
                            ))}
                          </Box>
                        )}

                        {hasNextPage && (
                          <Box
                            ref={loadMoreTriggerRef}
                            h="1px"
                            flexShrink={0}
                          />
                        )}
                      </>
                    ) : (
                      <Box
                        display="flex"
                        flexGrow={1}
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Text
                          textAlign="center"
                          color="blackAlpha.500"
                          fontSize="24px"
                          fontWeight="bold"
                        >
                          {hasActiveFilters ? (
                            <>
                              No{' '}
                              {selectedTab === 'INTAKE'
                                ? 'intakes'
                                : 'interviews'}{' '}
                              match your filters.
                              <br />
                              Try adjusting or clearing the filters above.
                            </>
                          ) : (
                            <>
                              Your{' '}
                              {selectedTab === 'INTAKE'
                                ? 'intakes'
                                : 'interviews'}{' '}
                              will appear here.
                              <br />
                              Start by clicking the black button at the top.
                              <br />
                              <br />
                              Happy interviewing!
                            </>
                          )}
                        </Text>
                      </Box>
                    )}
                  </Box>
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </Card>
      </Card>
    </Box>
  )
}

const InterviewCard: React.FC<{
  interview: ShortInterview
  hasTeam?: boolean
  selectedTab: InterviewType
  hasEvents?: boolean
  ref?: React.RefObject<HTMLDivElement>
}> = ({ interview, hasTeam, selectedTab, hasEvents, ref }) => {
  const navigate = useNavigate()
  const [isPresent, safeToRemove] = usePresence()

  const buttonBackgroundColorByDecision = (decision: string) => {
    if (decision === 'strong no') {
      return '#C00'
    } else if (decision === 'no') {
      return '#F60'
    } else if (decision === 'yes') {
      return '#9C0'
    } else if (decision === 'strong yes') {
      return '#090'
    }
  }

  const buttonTextColorByDecision = (decision: string) => {
    if (
      decision === 'strong no' ||
      decision === 'no' ||
      decision === 'strong yes'
    ) {
      return 'white'
    } else if (decision === 'yes') {
      return 'black'
    }
  }

  const [isFullTranscriptGenerating, setIsFullTranscriptGenerating] = useState(
    interview.isFullTranscriptGenerating || false,
  )
  const [recallStatus, setRecallStatus] = useState<RecallStatus | undefined>(
    interview.recallStatus || undefined,
  )

  useWaitForTranscriptGeneration({
    interviewId: interview.id,
    isFullTranscriptGenerating,
    setIsFullTranscriptGenerating,
  })
  useWaitForRecallFinish({
    interviewId: interview.id,
    recallStatus: recallStatus,
    setRecallStatus,
    setIsFullTranscriptGenerating,
  })

  const statusTextForInterview = useMemo(() => {
    if (interview.recallStatus === 'awaiting') {
      return 'scheduled'
    } else if (interview.recallStatus === 'recording') {
      return 'recording'
    } else if (interview.recallStatus === 'opted_out') {
      return 'opted out'
    } else if (isFullTranscriptGenerating) {
      return 'processing...'
    } else if (interview.hasLocalRecording) {
      return 'not uploaded'
    }
  }, [
    interview.recallStatus,
    isFullTranscriptGenerating,
    interview.hasLocalRecording,
  ])

  const isViewOnlyUser = useIsViewOnlyMember()
  const isATSConnected = useIsATSConnected()

  return (
    <Box
      key={interview.id}
      as={motion.div}
      ref={ref}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          ease: 'linear',
        },
      }}
      onAnimationComplete={() => !isPresent && safeToRemove()}
      className="px-3 py-2.5 flex items-center cursor-pointer hover:bg-[#EBEBEB] rounded-full justify-between min-w-0"
      onClick={() => {
        navigate(`/interview/${interview.id}`)
      }}
      transitionProperty={'background-color'}
      transitionDuration={'0.4s'}
      transitionTimingFunction={'ease'}
    >
      <Box
        display={'flex'}
        alignItems={'center'}
        minW={selectedTab === 'INTAKE' ? '210px' : '190px'}
        w={selectedTab === 'INTAKE' ? '290px' : '260px'}
        flexShrink={1}
      >
        {!interview.Candidate?.profilePictureURL &&
        interview.recallStatus === 'awaiting' ? (
          <Box w={'2.25rem'} h={'2.25rem'} />
        ) : (
          <Avatar
            src={interview.Candidate?.profilePictureURL || ''}
            w={'2.25rem'}
            h={'2.25rem'}
            icon={
              <LinkedinAvatarFallback
                h={'2.25rem'}
                w={'2.25rem'}
                rounded={'full'}
              />
            }
          />
        )}
        <Text ml={2.5} fontWeight={'450'}>
          {interview.Candidate?.fullName ||
            (interview.llmCandidateName ? (
              <Tooltip
                label={
                  isATSConnected
                    ? 'AI-detected candidate — not yet matched to ATS'
                    : 'AI-detected candidate name'
                }
                hasArrow
              >
                <Text
                  as="span"
                  color="gray.400"
                  fontStyle="italic"
                  cursor="help"
                >
                  {interview.llmCandidateName}
                </Text>
              </Tooltip>
            ) : (
              '—'
            ))}
        </Text>
      </Box>
      <Box
        minW={'120px'}
        w={'190px'}
        display={{
          base: 'none',
          lg: 'flex',
        }}
        flexDirection={'column'}
        justifyContent={'center'}
        gap={'2px'}
        flexShrink={1}
      >
        <Text fontWeight={'450'}>{interview.Role?.jobTitle || '—'}</Text>
        {interview.applicationStage && (
          <Text
            fontSize={'0.75rem'}
            fontWeight={'500'}
            color={'gray.500'}
            noOfLines={1}
          >
            {formatApplicationStage(interview.applicationStage)}
          </Text>
        )}
      </Box>
      {hasEvents && (
        <Box
          minW={'120px'}
          w={'210px'}
          display={{
            base: 'none',
            lg: 'block',
          }}
          flexShrink={1}
        >
          <Text fontWeight={'450'} overflow={'hidden'}>
            {interview.meetingName || '—'}
          </Text>
        </Box>
      )}
      <Box
        display={{
          base: 'none',
          xl: selectedTab === 'INTAKE' ? 'none' : 'block',
        }}
        minW={'100px'}
        w={'160px'}
        flexShrink={1}
      >
        {statusTextForInterview ? (
          <StatusBadge interview={interview} status={statusTextForInterview} />
        ) : (
          !!interview.decision && (
            <Box
              backgroundColor={buttonBackgroundColorByDecision(
                interview.decision,
              )}
              fontSize={'0.875rem'}
              fontWeight={'600'}
              rounded={'1.25rem'}
              px={3.5}
              py={1.5}
              alignItems={'center'}
              whiteSpace={'nowrap'}
              w={'fit-content'}
              color={buttonTextColorByDecision(interview.decision)}
            >
              {interview.decision}
            </Box>
          )
        )}
      </Box>
      {hasTeam && (
        <Box
          alignItems={'center'}
          minW={'120px'}
          w={'210px'}
          display={{
            base: 'none',
            md: 'flex',
          }}
          flexShrink={1}
        >
          {interview.Interviewers?.length > 1 ? (
            <AvatarGroup spacing={-2} max={3} size="brandMd">
              {interview.Interviewers.map((interviewer, index) => (
                <Avatar
                  key={index}
                  src={interviewer?.photoURL || ''}
                  name={interviewer?.displayName || ''}
                />
              ))}
            </AvatarGroup>
          ) : (
            <>
              <Avatar
                src={interview.Interviewers?.[0]?.photoURL || ''}
                name={interview.Interviewers?.[0]?.displayName || ''}
                w={'2.25rem'}
                h={'2.25rem'}
              />
              <Text ml={2.5} fontWeight={'450'}>
                {interview.Interviewers?.[0]?.displayName?.split(' ')?.[0] ||
                  '—'}
              </Text>
            </>
          )}
        </Box>
      )}
      <Text
        fontWeight={'450'}
        minW={'100px'}
        w={'140px'}
        display={{
          base: 'none',
          sm: 'block',
        }}
        flexShrink={1}
      >
        {interview.dateTimestamp
          ? dateFromTimestamp(interview.dateTimestamp)
          : '—'}
      </Text>
      {isViewOnlyUser ? (
        <Box w={'2.5rem'} h={'2.5rem'} flexShrink={0} />
      ) : (
        <DeleteInterviewButton interview={interview} />
      )}
    </Box>
  )
}

type BadgeProps = React.ComponentPropsWithRef<'div'> & { status: string }

const Badge = ({ status, ref, ...rest }: BadgeProps) => {
  return (
    <Box
      ref={ref as React.Ref<HTMLDivElement>}
      px="1.25rem"
      py="0.375rem"
      fontSize="0.875rem"
      rounded="2.5rem"
      border="1.5px solid #D9D9D9"
      whiteSpace="nowrap"
      w="fit-content"
      {...rest}
    >
      {status}
    </Box>
  )
}

const StatusBadge: React.FC<{
  interview: ShortInterview
  status: string
}> = ({ interview, status }) => {
  if (interview.optedOutByEmail) {
    return (
      <Tooltip
        rounded={'1.25rem'}
        py={2}
        px={4}
        bgColor={'blackAlpha.900'}
        w={'260px'}
        label={
          <>
            <b>{interview.optedOutByEmail}</b> opted out from the recording
          </>
        }
      >
        <Badge status={status} />
      </Tooltip>
    )
  }

  return <Badge status={status} />
}

const DeleteInterviewButton: React.FC<{
  interview: ShortInterview
}> = ({ interview }) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const user = useUser()
  const deleteInterview = useDeleteInterview(interview.id)

  return (
    <>
      <IconButton
        aria-label={'Delete interview'}
        icon={<DeleteIcon />}
        rounded={'full'}
        size={'md'}
        variant={'brand-solid-white'}
        onClick={(event) => {
          event.stopPropagation()
          onOpen()
        }}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {interview.recallStatus === 'awaiting'
              ? 'Cancel scheduled recording'
              : 'Confirm deletion'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {interview.recallStatus === 'awaiting'
              ? 'Cancel the scheduled recording'
              : 'Confirm deletion of interview'}
            {interview.Candidate?.fullName ? (
              <>
                {' '}
                with <b>{interview.Candidate?.fullName}</b>
              </>
            ) : (
              ''
            )}
            {interview.Role?.jobTitle && (
              <span>
                {' '}
                for <b>{interview.Role?.jobTitle}</b>
              </span>
            )}
            ?
          </ModalBody>

          <ModalFooter>
            <Button
              rounded={'1.25rem'}
              colorScheme={'red'}
              onClick={async () => {
                try {
                  setIsDeleting(true)
                  await deleteInterview.mutateAsync('manual')
                  await user.refetch()
                } catch (err) {
                  console.error(err)
                } finally {
                  setIsDeleting(false)
                  onClose()
                }
              }}
              isLoading={isDeleting}
            >
              Delete
            </Button>
            <Button
              onClick={onClose}
              rounded={'1.25rem'}
              variant="brand-solid-white"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default Home
